import type { MenuItems } from "../../common/Sidebar/types";
import {
  ResponseStorageObject,
  StorageObject,
} from "../api/instances/storageApi/types/object_types";
import { StorageObjectValue } from "../api/instances/storageApi/types/service_types";

const parseValue = (value: string) => {
  // prettier-ignore
  if (value === "null" || value === "") {
    return null;
  }
  return JSON.parse(value);
};

export const processObject = <T extends StorageObjectValue>(
  object: ResponseStorageObject,
): StorageObject<T> => {
  const value = parseValue(object?.value);
  const children = object?.children
    ?.map(processObject)
    .filter((child) => child !== null) as StorageObject<T>[];

  return {
    ...object,
    id: String(object.id),
    value: value,
    children: children,
  };
};

export const createStorageObjectApi = <T extends StorageObjectValue>(
  initialObjects: StorageObject<T>[],
) => {
  let objects = [...initialObjects];

  const getFilteredObjects = () => {
    const folders = objects
      .filter((object) => object?.value === null)
      .sort((a, b) => (Number(a.id) > Number(b.id) ? 1 : -1));
    const items = objects
      .filter((object) => object?.value !== null)
      .sort((a, b) => (Number(a.id) > Number(b.id) ? 1 : -1));
    return [...folders, ...items];
  };

  const getCountOfObjects = () => {
    const countOfObjects = objects.reduce((acc, object) => {
      if (object?.value !== null) {
        return acc + 1;
      } else {
        const children = object.children?.length || 0;
        return acc + children;
      }
    }, 0);
    return countOfObjects;
  };

  const getCountOfFolders = () =>
    objects.filter((object) => object?.value === null).length;

  const getRefTextForJson = (ref?: string) => {
    if (!ref) {
      return "";
    }
    return `${ref.slice(0, 6)}*************${ref.slice(-6)}`;
  };

  const findById = (id: string, items = objects): StorageObject<T> | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findById(id, item.children);
        if (found) return found;
      }
    }
    return null;
  };

  const findParent = (id: string, items = objects): StorageObject<T> | null => {
    for (const item of items) {
      if (item.children?.some((child) => child.id === id)) return item;
      if (item.children) {
        const parent = findParent(id, item.children);
        if (parent) return parent;
      }
    }
    return null;
  };

  const toMenuItems = (items = objects): MenuItems =>
    items.map((item) => ({
      id: item.id,
      name: item.friendly_name,
      value: item.value,
      isFolder: item?.value === null ? true : false,
      children: item?.children?.length ? toMenuItems(item.children) : null,
    }));

  const updateById = (
    id: string,
    updates: Partial<StorageObject<T>>,
    returnArray: boolean = false,
    items: StorageObject<T>[] = objects,
  ): StorageObject<T>[] | StorageObject<T> | null => {
    const updatedItems = items.map((item) => {
      if (item.id === id) {
        return { ...item, ...updates };
      }

      if (item.children && item.value === null) {
        const updatedChildren = updateById(id, updates, true, item.children);
        if (updatedChildren !== item.children) {
          return { ...item, children: updatedChildren as StorageObject<T>[] };
        }
      }

      return item;
    });

    if (items === objects) {
      objects = updatedItems;
    }

    return returnArray ? updatedItems : findById(id);
  };

  const updateObjects = (
    newObject: StorageObject<T>,
    items = objects,
  ): StorageObject<T>[] => {
    return items.map((item) => {
      if (item.id === newObject.id) {
        return { ...newObject };
      }
      if (item.children) {
        return {
          ...item,
          children: updateObjects(newObject, item.children),
        };
      }
      return { ...item };
    });
  };

  const addItem = (
    newItem: StorageObject<T>,
    parentId?: string,
    items = objects,
  ): StorageObject<T>[] => {
    if (!parentId) return [...items, newItem];
    return items.map((item) => {
      if (item.id === parentId)
        return { ...item, children: [...(item.children || []), newItem] };
      if (item.children)
        return { ...item, children: addItem(newItem, parentId, item.children) };
      return item;
    });
  };

  const removeById = (id: string, items = objects): StorageObject<T>[] => {
    return items
      .filter((item) => item.id !== id)
      .map((item) =>
        item.children
          ? { ...item, children: removeById(id, item.children) }
          : item,
      );
  };

  const moveItem = (itemId: string, newParentId?: string) => {
    const item = findById(itemId);
    if (!item) return objects;

    const newStorage = removeById(itemId);
    return addItem(item, newParentId, newStorage);
  };

  return {
    getFilteredObjects,
    getCountOfObjects,
    getCountOfFolders,
    getRefTextForJson,
    findById,
    findParent,
    toMenuItems,
    updateById,
    updateObjects,
    addItem,
    removeById,
    moveItem,
  };
};
